import { Box, Table, TableRow, TopBar } from '@sam/components';
import { TableHeader, TableKey } from '@sam/components/src/Table/Table.types';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Offer, Order, OrderState, updateSchambeckUserTableConfig, useData } from 'shared';
import { convertOffersIntoTableEntries } from '../../utils/order/Order.utils';
import { useUser } from '../../components/UserContext';
import { IconComponent, IconType } from '../../components/icons/IconComponent';

export const CustomerProjectOverview: React.FC = () => {
  const [filterValue, setFilterValue] = useState<string>('');
  const { axios, updateContextUser, user } = useUser();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [includeCompleted, toggleIncludeCompleted] = useState<boolean>(false);

  const { data: loadedCustomerProjects } = useData('CUSTOMER_PROJECT', {
    config: { fallbackData: [] },
  });

  const { data: loadedCustomers } = useData('CUSTOMER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedOrders } = useData('ORDER_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerLocations } = useData('CUSTOMER_LOCATION_ALL', {
    config: { fallbackData: [] },
  });
  const { data: loadedCustomerContacts } = useData('CUSTOMER_USERS_ALL', {
    config: { fallbackData: [] },
  });

  /**
   * Holds the entries as a TableRow Array
   */
  const rows: TableRow[] = useMemo((): TableRow[] => {
    const ordersToShow: Order[] = includeCompleted
      ? loadedOrders
      : loadedOrders.filter((order) => order.orderState !== OrderState.PROJECT_COMPLETED);
    const offersToShow: Offer[] = includeCompleted
      ? loadedCustomerProjects
      : loadedCustomerProjects.filter((offer) =>
          ordersToShow.some((order) => order.id === offer.orderId),
        );
    return convertOffersIntoTableEntries(
      offersToShow,
      ordersToShow,
      loadedCustomers,
      loadedCustomerLocations,
      loadedCustomerContacts,
      navigate,
    );
  }, [
    loadedCustomerContacts,
    loadedCustomerLocations,
    loadedCustomerProjects,
    loadedCustomers,
    loadedOrders,
    navigate,
    includeCompleted,
  ]);

  return (
    <>
      <TopBar title="Projekt-Bestellungen" onSearch={setFilterValue}>
        <IconComponent
          type={IconType.DISABLED}
          style={{ color: includeCompleted ? '#bc2e46' : '' }}
          onClick={() => toggleIncludeCompleted(!includeCompleted)}
        />
        <IconComponent type={IconType.ADD} onClick={() => navigate('/customer/project/edit')} />
      </TopBar>
      <Box>
        <Table
          tableConfig={user.tableConfig?.get(TableKey.CUSTOMER_PROJECT_OVERVIEW)}
          updateTableConfig={(updatedHeader) => {
            updateSchambeckUserTableConfig(
              axios,
              user,
              TableKey.CUSTOMER_PROJECT_OVERVIEW,
              updatedHeader,
            ).then((updatedUser) => updatedUser && updateContextUser(updatedUser));
          }}
          filterValue={filterValue}
          rows={rows}
          header={
            t('pages.order.overview.tableHeader', {
              returnObjects: true,
            }) as TableHeader[]
          }
        />
      </Box>
    </>
  );
};
